import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';

import { Tag } from 'features/common/types';
import { getTagId } from 'features/common/utils/tagUtils';
import {
	PunchupTagManager,
	CreateTagData,
	UpdateTagData,
} from 'features/common/components/Punchup/composites/editors/PunchupTagManager';
import { useOptionalAccordionSection } from 'features/common/components/Punchup/primitives/layout/PunchupAccordion';
import { queryClient } from 'index';
import {
	useTagsApi,
	TagType,
	CreateTagRequest,
	addTagsToTicketType,
	createTag,
	removeTagsFromTicketType,
	updateTag,
} from 'features/common/hooks/useTagsApi';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TicketTagsFieldProps {
	ticketTags?: Tag[];
	ticketTypeId?: number;
	/** True while the ticket has not yet been persisted (no ID). Hides the tag UI. */
	isNewTicket?: boolean;
	/** Maximum tags. Defaults to 10. */
	maxItems?: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * TicketTagsField
 *
 * Thin wrapper around PunchupTagManager for the Ticket Type form.
 * Shows a gate message while the ticket has no saved ID.
 *
 * Two modes — chosen automatically based on form context:
 *
 * Staged mode (accordion inline form): detected when the parent form provides a
 * `tag_ids: number[]` field. Tag assignments are accumulated in that form field
 * and only persisted when the form's Save button is submitted. No API calls for
 * add/remove — only for tag create/update (registry operations are always immediate).
 *
 * Immediate mode (standalone sub-page): detected when no `tag_ids` field is in
 * the parent form. Add/remove call the API directly, as before.
 */
const TicketTagsField: FC<TicketTagsFieldProps> = ({
	ticketTags = [],
	ticketTypeId,
	isNewTicket = false,
	maxItems = 10,
}) => {
	const { t } = useTranslation();
	const section = useOptionalAccordionSection();
	const { tags: allTags, refetch: refetchTags } = useTagsApi();
	const formContext = useFormContext<{ tag_ids?: number[] }>();

	// Staged mode: parent form has a `tag_ids` field.
	// Immediate mode: no such field (standalone sub-page context).
	const stagedTagIds: number[] | undefined = formContext?.watch('tag_ids');
	const isStaged = stagedTagIds !== undefined;

	const availableTags = useMemo(() => {
		if (!Array.isArray(allTags)) return [];
		return allTags
			.filter((tag: Tag) => tag.tag_type === TagType.TICKET_TYPE)
			.map((tag: Tag) => ({ ...tag, id: getTagId(tag) }));
	}, [allTags]);

	// In staged mode, derive selectedTags from the form field so the chip strip
	// reflects staged (not yet persisted) changes.
	const selectedTags = isStaged
		? availableTags.filter((t) => (stagedTagIds as number[]).includes(getTagId(t)))
		: ticketTags;

	// ── Handlers ─────────────────────────────────────────────────────────────

	const handleAdd = async (tagId: number) => {
		if (isStaged) {
			const current = (formContext.getValues('tag_ids') as number[] | undefined) ?? [];
			formContext.setValue('tag_ids', [...current, tagId], { shouldDirty: true });
			// RHF does not reliably notify subscribers when shouldDirty fires on an
			// unregistered field — call setDirty directly as a guaranteed fallback.
			section?.setDirty(true);
			return;
		}
		if (!ticketTypeId) return;
		try {
			await addTagsToTicketType(ticketTypeId, { tag_ids: [tagId] });
			queryClient.invalidateQueries({ queryKey: ['ticket', String(ticketTypeId)] });
		} catch {
			section?.flash('error', { title: t('tags-add-error', 'Failed to add tag') });
			throw new Error('add failed');
		}
	};

	const handleRemove = async (tagId: number) => {
		if (isStaged) {
			const current = (formContext.getValues('tag_ids') as number[] | undefined) ?? [];
			formContext.setValue(
				'tag_ids',
				current.filter((id) => id !== tagId),
				{ shouldDirty: true }
			);
			section?.setDirty(true);
			return;
		}
		if (!ticketTypeId) return;
		try {
			await removeTagsFromTicketType(ticketTypeId, { tag_ids: [tagId] });
			queryClient.invalidateQueries({ queryKey: ['ticket', String(ticketTypeId)] });
		} catch {
			section?.flash('error', { title: t('tags-remove-error', 'Failed to remove tag') });
			throw new Error('remove failed');
		}
	};

	const handleCreate = async (data: CreateTagData): Promise<Tag> => {
		try {
			const newTag = await createTag({ ...data, tag_type: TagType.TICKET_TYPE } as CreateTagRequest);
			await refetchTags();
			if (isStaged) {
				// Stage the assignment — do not call addTagsToTicketType yet.
				const current = (formContext.getValues('tag_ids') as number[] | undefined) ?? [];
				formContext.setValue('tag_ids', [...current, getTagId(newTag)], { shouldDirty: true });
				section?.setDirty(true);
			} else {
				// Immediate mode: assign right away.
				if (ticketTypeId) {
					await addTagsToTicketType(ticketTypeId, { tag_ids: [getTagId(newTag)] });
					queryClient.invalidateQueries({ queryKey: ['ticket', String(ticketTypeId)] });
				}
			}
			return newTag;
		} catch {
			section?.flash('error', { title: t('tags-create-error', 'Failed to create tag') });
			throw new Error('create failed');
		}
	};

	const handleUpdate = async (tagId: number, data: UpdateTagData) => {
		// Tag property updates (name/color/description) are always immediate —
		// they modify the tag registry, not the entity's assignment list.
		try {
			await updateTag(tagId, data);
			await refetchTags();
			if (ticketTypeId) queryClient.invalidateQueries({ queryKey: ['ticket', String(ticketTypeId)] });
		} catch {
			section?.flash('error', { title: t('tags-update-error', 'Failed to update tag') });
			throw new Error('update failed');
		}
	};

	// ── Render ───────────────────────────────────────────────────────────────

	return (
		<div>
			<label className="punchupFormLabel">{t('tags-ticket-tags', 'Tags')}</label>

			{isNewTicket || (!ticketTypeId && !isStaged) ? (
				<p className="punchupFormDescription" style={{ marginTop: 4 }}>
					{t('tags-save-ticket-first', 'Save the ticket first to manage tags.')}
				</p>
			) : (
				<PunchupTagManager
					availableTags={availableTags}
					selectedTags={selectedTags}
					onAdd={handleAdd}
					onRemove={handleRemove}
					onCreate={handleCreate}
					onUpdate={handleUpdate}
					maxItems={maxItems}
					onDirtyChange={(isDirty) => section?.setDirty(isDirty)}
				/>
			)}
		</div>
	);
};

export { TicketTagsField };
